/**
 * Signing out of one app signs the browser out of the suite.
 *
 * Two facts make this necessary. `app.airclou.com` hosts several apps in **one**
 * localStorage, so `helpdesk:authToken`, `playbook:authToken` and friends sit
 * side by side on the same origin. And the `airclou_session` cookie is a single
 * slot for `.airclou.com`, so signing out invalidates the session behind *all*
 * of them at once.
 *
 * Each app's own sign-out swept only its own prefix, so the others kept a token
 * the cookie no longer backs — a credential with no session behind it, which is
 * the stale-identity class this plan exists to close, arriving by a different
 * road. And a tab that was already open kept its token until something remounted
 * it, which for a backgrounded tab can be never.
 *
 * See apps/auth/docs/session-credential-standardization-plan.md (ID-7).
 */
/**
 * The key suffixes sign-out owns, per app prefix.
 *
 * An allowlist rather than "everything under `{app}:`" on purpose: the same
 * namespace holds ordinary preferences (`helpdesk:theme`,
 * `playbook:sidebarCollapsed`) that signing out has no business resetting. Keys
 * are matched as `{app}:{suffix}` with a lowercase app prefix.
 */
export const SUITE_AUTH_KEY_SUFFIXES = [
    'authToken',
    'workspaceId',
    'workspaceName',
    'workspaceSlug',
    'companyId',
    'companyName',
    'lastWorkspaceId',
    'workspacesMyTrueCacheV1',
    'isRealUser',
    'impersonationToken',
];
// Digits are allowed in the app prefix even though no app uses one today — the
// suffix allowlist is what actually constrains this, and a prefix pattern that
// silently stops matching a future app name would fail open (keys left behind),
// which is the direction that costs something.
const SUITE_AUTH_KEY_PATTERN = new RegExp(`^[a-z][a-z0-9-]*:(${SUITE_AUTH_KEY_SUFFIXES.join('|')})$`);
/** Does this localStorage key belong to some app's session on this origin? */
export function isSuiteAuthKey(key) {
    return SUITE_AUTH_KEY_PATTERN.test(key);
}
/**
 * Drop every app's session keys on this origin, not just the calling app's.
 * Returns what it removed, which is what the tests assert on.
 */
export function clearSuiteAuthKeys() {
    const removed = [];
    try {
        // Snapshot the keys first: removing while iterating localStorage's live
        // index skips entries.
        for (const key of Object.keys(localStorage)) {
            if (isSuiteAuthKey(key))
                removed.push(key);
        }
        for (const key of removed) {
            localStorage.removeItem(key);
        }
    }
    catch {
        // Private mode, disabled storage: nothing to clear and nothing to report.
    }
    return removed;
}
/**
 * The whole sign-out, in one call: clear every app's session keys on this
 * origin AND tell the other tabs. The two halves are only correct together —
 * clearing without broadcasting leaves a backgrounded sibling tab holding a
 * dead credential until a remount that may never come (ID-7). Every sign-out
 * path uses this; the one legitimate clear-without-broadcast site is the
 * `onSuiteSignOut` *receive* handler, where re-broadcasting would echo the
 * sign-out between tabs forever.
 */
export function signOutOfSuite(reason) {
    clearSuiteAuthKeys();
    broadcastSignOut(reason);
}
// ---------------------------------------------------------------------------
// Cross-tab notification
// ---------------------------------------------------------------------------
const CHANNEL_NAME = 'airclou:session';
const SIGNED_OUT = 'signed-out';
/**
 * One channel per document, created lazily.
 *
 * Held as a single object deliberately: a `BroadcastChannel` never delivers a
 * message to the object that sent it, so the tab doing the signing out does not
 * hear its own broadcast and re-enter sign-out.
 */
let channel = null;
function getChannel() {
    if (typeof BroadcastChannel === 'undefined')
        return null;
    if (!channel) {
        try {
            channel = new BroadcastChannel(CHANNEL_NAME);
        }
        catch {
            return null;
        }
    }
    return channel;
}
/** Tell every other tab on this origin that the session is over. */
export function broadcastSignOut(reason) {
    try {
        getChannel()?.postMessage({ type: SIGNED_OUT, reason });
    }
    catch {
        // Best-effort. The local sign-out has already happened; a tab that misses
        // the message still finds no token at its next mount.
    }
}
/**
 * Run `handler` when another tab signs out. Returns the unsubscribe function.
 *
 * A no-op where `BroadcastChannel` is unavailable (older Safari, some embedded
 * webviews) — those tabs fall back to the pre-existing behaviour of noticing at
 * their next mount.
 */
export function onSuiteSignOut(handler) {
    const ch = getChannel();
    if (!ch)
        return () => { };
    const listener = (event) => {
        // The reason travels with the message because the receiving tab acts on it:
        // a deliberate sign-out may clear the user's in-progress work, an idle one
        // must not — they did not choose to leave. Without it every sibling tab
        // treated an idle sign-out as manual and destroyed unsent replies.
        if (event.data?.type === SIGNED_OUT)
            handler(event.data?.reason);
    };
    ch.addEventListener('message', listener);
    return () => ch.removeEventListener('message', listener);
}
